// Voice-session lifecycle for manufacturing dictation pads.
//
// Uses the same Deepgram streaming engine as Lead Workspace so a brief
// pause finalizes a phrase without Chrome's Web Speech "stop listening"
// ding (which drops the next words while the mic restarts).
import { useCallback, useMemo, useState } from "react";
import { useDeepgramHoldMic } from "@/lib/asr/useDeepgramHoldMic";
import { joinTranscriptChunks } from "@/lib/speech/speechDictationTranscript";
import {
  mapHoldMicToDictateState,
  mediaDevicesSupported,
  type DictateSessionState,
} from "./manufacturingDictateSessionState";

export type { DictateSessionState };

export type ManufacturingDictateSession = {
  isSupported: boolean;
  state: DictateSessionState;
  /** Live transcript draft. Updates as Deepgram finalizes each phrase. */
  transcript: string;
  error: string | null;
  start: () => void;
  stop: () => void;
  reset: () => void;
};

export function useManufacturingDictateSession(): ManufacturingDictateSession {
  const [transcript, setTranscript] = useState("");
  const [awaitingCommit, setAwaitingCommit] = useState(false);
  const isSupported = mediaDevicesSupported();

  const onAppend = useCallback((chunk: string) => {
    setTranscript((prev) => joinTranscriptChunks(prev, chunk));
  }, []);

  const mic = useDeepgramHoldMic({ onAppend });

  const state = useMemo(
    () =>
      mapHoldMicToDictateState({
        isSupported,
        micStatus: mic.status,
        awaitingCommit,
      }),
    [isSupported, mic.status, awaitingCommit],
  );

  const start = useCallback(() => {
    setAwaitingCommit(false);
    setTranscript("");
    void mic.start();
  }, [mic.start]);

  const stop = useCallback(() => {
    void (async () => {
      await mic.stop();
      setAwaitingCommit(true);
    })();
  }, [mic.stop]);

  const reset = useCallback(() => {
    setAwaitingCommit(false);
    setTranscript("");
  }, []);

  return {
    isSupported,
    state,
    transcript,
    error: mic.error,
    start,
    stop,
    reset,
  };
}
